import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Fade from '@mui/material/Fade';

const ImageComponent = React.forwardRef(({ src, alt, className, isLoading: loadingProp, setIsLoading: setLoadingProp, ...props }, ref) => {
  const [internalIsLoading, setInternalIsLoading] = useState(true);
  const actualIsLoading = loadingProp !== undefined ? loadingProp : internalIsLoading;

  const srcSetBase = src.replace(/\.(jpg|jpeg|png|webp)$/, '');

  const handleImageLoad = useCallback(() => {
    if (loadingProp === undefined) {
      setInternalIsLoading(false);
    }
    if (setLoadingProp) {
      setLoadingProp(false);
    }
  }, [loadingProp, setLoadingProp]);

  const handleImageError = useCallback((error) => {
    console.error("Error loading image:", error);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = handleImageLoad;
    img.onerror = handleImageError;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, handleImageLoad, handleImageError]);

  const transitionDuration = 1677;

  return (
    <>
      {actualIsLoading ? (
        <Skeleton animation="wave" variant="rectangular" width="100%" height={420} />
      ) : (
        <Fade in={!actualIsLoading} timeout={transitionDuration}>
          <picture>
            <source
              srcSet={`
                            ${srcSetBase}_50.webp 1232w,
                            ${srcSetBase}_33.webp 813w,
                            ${srcSetBase}_25.webp 616w,
                            ${srcSetBase}.webp 2464w
                        `}
              sizes={`
                            (max-width: 813px) 100vw,
                            (max-width: 1232px) 50vw,
                            25vw
                        `}
              type="image/webp"
            />
            <source
              srcSet={`
                            ${srcSetBase}_50.jpg 1232w,
                            ${srcSetBase}_33.jpg 813w,
                            ${srcSetBase}_25.jpg 616w,
                            ${srcSetBase}.jpg 2464w
                        `}
              sizes={`
                            (max-width: 813px) 100vw,
                            (max-width: 1232px) 50vw,
                            25vw
                        `}
            />
            <img
              ref={ref}
              src={`${srcSetBase}.jpg`}
              alt={alt}
              loading="lazy"
              className={className}
              style={{
                width: '100%',
                height: '420px',
                objectFit: 'cover',
                display: 'block'
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </picture>
        </Fade>
      )}
    </>
  );
});

export default ImageComponent;